import { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import { Close, Refresh, Search, Visibility } from "@mui/icons-material";
import { getEmailDeliveryLog, getEmailDeliveryLogs } from "../api-services/EmailDeliveryLogApi";
import { EmailDeliveryLogDetail, EmailDeliveryLogQuery, EmailDeliveryLogSummary } from "../helpers/InterfacesServer";
import { useAppSelector } from "../store/store";

const initialQuery: EmailDeliveryLogQuery = { pageNumber: 1, pageSize: 25 };

export function EmailDeliveryLogs() {
    const user = useAppSelector(state => state.loggedOnUserProfile.value);
    const authorised = user?.isERMTeamMember === true || user?.isAdmin === true;
    const [query, setQuery] = useState<EmailDeliveryLogQuery>(initialQuery);
    const [rows, setRows] = useState<EmailDeliveryLogSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selected, setSelected] = useState<EmailDeliveryLogDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = useCallback(async () => {
        if (!authorised) return;
        setLoading(true);
        try {
            const result = await getEmailDeliveryLogs(query);
            setRows(result.items);
            setTotal(result.totalCount);
        } finally {
            setLoading(false);
        }
    }, [authorised, query]);

    useEffect(() => { load(); }, [load]);

    function applyFilters() {
        setQuery(previous => ({
            ...previous,
            pageNumber: 1,
            search: search.trim() || undefined,
            dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
            dateTo: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined
        }));
    }

    function clearFilters() {
        setSearch('');
        setDateFrom('');
        setDateTo('');
        setQuery(initialQuery);
    }

    async function openDetails(id: number) {
        setDetailLoading(true);
        try { setSelected(await getEmailDeliveryLog(id)); }
        finally { setDetailLoading(false); }
    }

    if (!authorised) {
        return <Paper className="Form" sx={{ p: 3 }}>
            <Alert severity="error">Email delivery logs are restricted to ERM team members and administrators.</Alert>
        </Paper>;
    }

    return <Paper className="Form" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} mb={2}>
            <Box flexGrow={1}>
                <Typography variant="h5">Email Delivery Logs</Typography>
                <Typography color="textSecondary">Review every email attempt, its delivery result, and the provider response or failure reason.</Typography>
            </Box>
            <Button startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        </Stack>

        <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Recipient, sender, subject, or reason" value={search}
                    onChange={event => setSearch(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') applyFilters(); }} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <TextField select fullWidth size="small" label="Status" value={query.status ?? ''}
                    onChange={event => setQuery(previous => ({ ...previous, pageNumber: 1, status: event.target.value || undefined }))}
                    SelectProps={{ native: true }}>
                    <option value="">All statuses</option>
                    <option value="SUCCESS">Successful</option>
                    <option value="FAILED">Failed</option>
                    <option value="PENDING">Pending</option>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <TextField fullWidth size="small" label="From date" type="date" value={dateFrom}
                    onChange={event => setDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <TextField fullWidth size="small" label="To date" type="date" value={dateTo}
                    onChange={event => setDateTo(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="contained" startIcon={<Search />} onClick={applyFilters}>Search</Button>
                    <Button onClick={clearFilters}>Clear</Button>
                </Stack>
            </Grid>
        </Grid>

        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Date attempted</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Provider</TableCell>
                        <TableCell>Result / reason</TableCell>
                        <TableCell align="right">Details</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={28} /></TableCell></TableRow>}
                    {!loading && rows.map(row => <TableRow hover key={row.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.attemptedDate)}</TableCell>
                        <TableCell><StatusChip status={row.status} /></TableCell>
                        <TableCell sx={{ maxWidth: 240 }}><Ellipsis value={row.toAddresses} /></TableCell>
                        <TableCell sx={{ maxWidth: 300 }}><Ellipsis value={row.subject} /></TableCell>
                        <TableCell>{row.provider}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}><Ellipsis value={row.reason} /></TableCell>
                        <TableCell align="right">
                            <Tooltip title="View email details"><IconButton size="small" onClick={() => openDetails(row.id)}><Visibility /></IconButton></Tooltip>
                        </TableCell>
                    </TableRow>)}
                    {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" py={3}>No email delivery logs match the selected filters.</Typography>
                    </TableCell></TableRow>}
                </TableBody>
            </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={query.pageNumber - 1} rowsPerPage={query.pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, page) => setQuery(previous => ({ ...previous, pageNumber: page + 1 }))}
            onRowsPerPageChange={event => setQuery(previous => ({ ...previous, pageNumber: 1, pageSize: Number(event.target.value) }))} />

        <Dialog open={detailLoading || selected != null} onClose={() => !detailLoading && setSelected(null)} maxWidth="md" fullWidth>
            <DialogTitle>
                Email delivery details
                <IconButton onClick={() => setSelected(null)} sx={{ position: 'absolute', right: 8, top: 8 }} disabled={detailLoading}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {detailLoading && <Box textAlign="center" py={5}><CircularProgress /></Box>}
                {selected && <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                        <StatusChip status={selected.status} />
                        <Typography><b>Provider:</b> {selected.provider}</Typography>
                        <Typography><b>Attempted:</b> {formatDate(selected.attemptedDate)}</Typography>
                        <Typography><b>Duration:</b> {selected.durationMilliseconds == null ? '—' : `${selected.durationMilliseconds} ms`}</Typography>
                    </Stack>
                    <Detail label="Result / reason" value={selected.reason} />
                    <Detail label="From" value={selected.fromAddress || '—'} />
                    <Detail label="To" value={selected.toAddresses || '—'} />
                    <Detail label="CC" value={selected.ccAddresses || '—'} />
                    <Detail label="BCC" value={selected.bccAddresses || '—'} />
                    <Detail label="Subject" value={selected.subject} />
                    <Detail label="Attachments" value={selected.attachmentNames || 'None'} />
                    <TextField label="Email body (HTML)" value={selected.body} multiline minRows={8} maxRows={18} fullWidth
                        InputProps={{ readOnly: true }} />
                </Stack>}
            </DialogContent>
            <DialogActions><Button onClick={() => setSelected(null)} disabled={detailLoading}>Close</Button></DialogActions>
        </Dialog>
    </Paper>;
}

function StatusChip({ status }: { status: string }) {
    const color = status === 'SUCCESS' ? 'success' : status === 'FAILED' ? 'error' : 'warning';
    const label = status === 'SUCCESS' ? 'Successful' : status === 'FAILED' ? 'Failed' : 'Pending';
    return <Chip size="small" color={color} label={label} />;
}

function Ellipsis({ value }: { value: string }) {
    return <Tooltip title={value}><Typography noWrap>{value}</Typography></Tooltip>;
}

function Detail({ label, value }: { label: string, value: string }) {
    return <Box><Typography variant="caption" color="textSecondary">{label}</Typography><Typography sx={{ wordBreak: 'break-word' }}>{value}</Typography></Box>;
}

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '—';
}
